import { Component, ElementRef, HostListener, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ChatService, ChatWindow } from '../../services/chat.service';
import { ListState, listStateOf } from '../list-state/list-state.component';

@Component({
  selector: 'app-chat-dock',
  templateUrl: './chat-dock.component.html',
  styleUrls: ['./chat-dock.component.scss'],
})
export class ChatDockComponent implements OnInit, OnDestroy {
  public lightboxUrl: string | null = null;
  /** Odległość od góry listy (px), przy której zaczynamy dociągać starsze wiadomości. */
  private readonly LOAD_OLDER_THRESHOLD = 60;

  @ViewChildren('dockMessages') private messagesEls?: QueryList<ElementRef<HTMLElement>>;
  private msgSub?: Subscription;

  constructor(
    public chat: ChatService,
    private router: Router,
  ) {}

  /**
   * Stan listy kontaktow w panelu doku. `ownEmpty`, bo panel ma wlasny komunikat
   * „Brak kontaktow" — dotad pokazywany takze w trakcie pobierania, czyli zanim
   * bylo wiadomo, czy jakiekolwiek kontakty istnieja.
   */
  get contactsState(): ListState {
    return listStateOf(!this.chat.conversationsLoaded, this.chat.contacts, { ownEmpty: true });
  }

  ngOnInit(): void {
    // Scroll-management jak w chat-page, per okno doku.
    this.msgSub = this.chat.messageAdded.subscribe(e => {
      const el = this.elFor(e.win);
      if (!el) { return; }
      if (e.initial || e.mine || this.isNearBottom(el)) {
        this.scrollToBottom(e.win);
        e.win.showJump = false;
        e.win.jumpCount = 0;
      } else {
        e.win.showJump = true;
        e.win.jumpCount = (e.win.jumpCount || 0) + 1;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.msgSub) { this.msgSub.unsubscribe(); }
  }

  private elFor(win: ChatWindow): HTMLElement | null {
    const idx = this.chat.expandedWindows.indexOf(win);
    const arr = this.messagesEls ? this.messagesEls.toArray() : [];
    return idx >= 0 && arr[idx] ? arr[idx].nativeElement : null;
  }

  private isNearBottom(el: HTMLElement): boolean {
    return el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  }

  private scrollToBottom(win: ChatWindow): void {
    const scroll = () => {
      const el = this.elFor(win);
      if (el) { el.scrollTop = el.scrollHeight; }
    };
    // Drugi przebieg domyka przypadki, gdy fonty/obrazki podniosą scrollHeight
    // tuż po pierwszym ustawieniu scrollTop.
    setTimeout(scroll, 0);
    setTimeout(scroll, 150);
  }

  onScroll(win: ChatWindow): void {
    const el = this.elFor(win);
    if (win.showJump && el && this.isNearBottom(el)) {
      win.showJump = false;
      win.jumpCount = 0;
    }
    if (el) { this.maybeLoadOlder(win, el); }
  }

  /**
   * Przewinięcie pod sam początek listy dociąga starszą porcję historii — jak w chat-page,
   * tylko per okno doku. Zapamiętujemy odległość od DOŁU (niezmiennik przy dopisywaniu treści
   * na górze), więc po doklejeniu widok zostaje na tej samej wiadomości.
   */
  private maybeLoadOlder(win: ChatWindow, el: HTMLElement): void {
    if (el.scrollTop > this.LOAD_OLDER_THRESHOLD) { return; }
    const distFromBottom = el.scrollHeight - el.scrollTop;
    this.chat.loadOlder(win, () => {
      // setTimeout: jak w scrollToBottom — poczekaj, aż Angular dorenderuje nowe wiadomości.
      // elFor() liczone ponownie: okno mogło się w międzyczasie zminimalizować (wtedy null).
      setTimeout(() => {
        const cur = this.elFor(win);
        if (cur) { cur.scrollTop = cur.scrollHeight - distFromBottom; }
      }, 0);
    });
  }

  jumpToLatest(win: ChatWindow): void {
    win.showJump = false;
    win.jumpCount = 0;
    this.scrollToBottom(win);
  }

  onImageLoad(win: ChatWindow): void {
    const el = this.elFor(win);
    if (el && this.isNearBottom(el)) { this.scrollToBottom(win); }
  }

  // Na pełnoekranowej stronie czatu (/chat) dok jest zbędny — ukrywamy go.
  get onChatPage(): boolean {
    return (this.router.url || '').split('?')[0].startsWith('/chat');
  }

  /**
   * Zwija / rozwija dok. Zwinięcie chowa wyłącznie KAFLE — rozwinięte okna zostają na
   * ekranie (patrz `expandedWindows`, które nie zależy już od `dockCollapsed`).
   *
   * Dlatego nie ma tu nic poza przełączeniem flagi: żadne `.chat-messages` nie jest
   * usuwane ani odtwarzane przez `*ngFor`, więc pozycja scrolla i licznik „przejdź do
   * najnowszych" zostają nietknięte. Przywracane kafle to same awatary — nie mają
   * historii do przewinięcia.
   */
  toggleDock(): void {
    this.chat.dockCollapsed = !this.chat.dockCollapsed;
  }

  onKey(event: KeyboardEvent, win: ChatWindow): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.chat.send(win);
    } else {
      this.chat.notifyTyping(win);
    }
  }

  onPaste(event: ClipboardEvent, win: ChatWindow): void {
    const items = event.clipboardData && event.clipboardData.items;
    if (!items) { return; }
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file' && items[i].type.indexOf('image/') === 0) {
        event.preventDefault();
        const file = items[i].getAsFile();
        if (file) { this.readAndSend(file, win); }
        return;
      }
    }
  }

  onPickFile(event: Event, win: ChatWindow): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (file) { this.readAndSend(file, win); }
    input.value = '';
  }

  private readAndSend(file: File, win: ChatWindow): void {
    // Podpis czytamy w momencie wyboru pliku (jak dotychczas) — konwersja jest
    // asynchroniczna i tekst dopisany w jej trakcie nie powinien zniknąć z pola.
    const caption = (win.draft || '').trim();
    win.error = undefined;
    // Walidacja i ewentualna konwersja w ChatService — wspólne z widokiem /chat.
    this.chat.prepareAttachment(file).then(
      dataUrl => {
        this.chat.sendImage(win, dataUrl, caption);
        if ((win.draft || '').trim() === caption) { win.draft = ''; }
      },
      errorKey => { win.error = String(errorKey); }
    );
  }

  openImage(m: any): void {
    if (m && m.attachmentUrl && !m.pending && !m.error) { this.lightboxUrl = m.attachmentUrl; }
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeLightbox();
  }

  initials(name: string): string {
    if (!name) { return '?'; }
    return name.split(' ').filter(Boolean).map(p => p.charAt(0)).slice(0, 2).join('').toUpperCase();
  }

  time(ts: number): string {
    const d = new Date(ts);
    return ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2);
  }

  trackByPeer(_i: number, win: ChatWindow) {
    return win.peer.id;
  }
}
